import { z } from 'zod';
import { uuid } from './common.js';
import { imageDto } from './shopfront.js';
/** Phase 3, P3.1 (docs/design/11-phase-3-plan.md): seasons, assortments and buyers. */
export const GU_OCCASIONS = ['christmas', 'easter', 'occasion'];
export const guOccasion = z.enum(GU_OCCASIONS);
export const GU_OCCASION_LABELS = { christmas: 'Jul', easter: 'Påske', occasion: 'Anledning' };
const minor = z.number().int().min(0);
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** All amounts in øre excl. VAT. */
export const guFees = z.object({
    giftCardFreightMinor: minor,
    invoiceFeeMinor: minor,
    carryingSupportMinor: minor,
    extraAddressFeeMinor: minor,
    freeExtraAddresses: z.number().int().min(0),
    minimumRevenueMinor: minor,
    invoiceFromMinor: minor,
    adjustUp: z.boolean(),
    adjustDown: z.boolean(),
    adjustUpFeeMinor: minor,
    adjustDownFeeMinor: minor,
    minCodesForAdjust: z.number().int().min(0),
    giftCountWarning: z.number().int().min(0),
    giftCountWarningText: z.string().max(500),
});
export const guShipping = z.object({
    parcelShopPerCodeMinor: minor,
    homePerCodeMinor: minor,
    businessPerCodeMinor: minor,
    businessBands: z.array(z.object({ from: z.number().int().min(0), to: z.number().int().min(0).nullable(), priceMinor: minor })).max(20),
    businessFixedPerCodeMinor: minor.nullable(),
    /** Delivery dates buyers can choose (the old checkout hard-coded these per year). Null = no limit. */
    deliveryFrom: day.nullable().default(null),
    deliveryTo: day.nullable().default(null),
    /** ISO weekdays a delivery can be booked on (1 = Monday … 5 = Friday): "we ship by the Friday of the chosen week". */
    deliveryWeekdays: z.array(z.number().int().min(1).max(7)).min(1).default([5]),
    /** The earliest delivery is this many days after the order. */
    minLeadDays: z.number().int().min(0).max(120).default(14),
    /** A shop closes at least this many days before its delivery date. */
    closeDaysBeforeDelivery: z.number().int().min(0).max(60).default(14),
});
/**
 * Gaveuniverset's known values (its seeds and the 2022 update), as the start of a new season;
 * the live values sit in Gaveuniverset's database and are entered in the admin.
 */
export function guSeasonDefaults(occasion) {
    const kr = (n) => n * 100;
    const bands = (list) => list.map((p, i) => ({ from: i * 50, to: i < list.length - 1 ? i * 50 + 49 : null, priceMinor: kr(p) }));
    return {
        fees: {
            giftCardFreightMinor: kr(250),
            invoiceFeeMinor: kr(175),
            carryingSupportMinor: kr(445),
            extraAddressFeeMinor: kr(595),
            freeExtraAddresses: 0,
            minimumRevenueMinor: 0,
            invoiceFromMinor: 0,
            adjustUp: true,
            adjustDown: false,
            adjustUpFeeMinor: 0,
            adjustDownFeeMinor: 0,
            minCodesForAdjust: 0,
            giftCountWarning: 0,
            giftCountWarningText: '',
        },
        shipping: {
            parcelShopPerCodeMinor: kr(49),
            homePerCodeMinor: kr(69),
            businessPerCodeMinor: kr(79),
            // Occasion shops pay business delivery per code; Christmas and Easter by bands.
            businessBands: occasion === 'christmas' ? bands([595, 995, 1495, 1995, 2495]) : occasion === 'easter' ? bands([79, 159, 239, 325, 495]) : [],
            businessFixedPerCodeMinor: null,
            deliveryFrom: null,
            deliveryTo: null,
            deliveryWeekdays: [5],
            minLeadDays: 14,
            closeDaysBeforeDelivery: 14,
        },
    };
}
export const guSeasonDto = z.object({
    id: uuid,
    occasion: guOccasion,
    year: z.number().int(),
    name: z.string(),
    salesFrom: z.string().nullable(),
    salesTo: z.string().nullable(),
    opensFrom: z.string().nullable(),
    closesBy: z.string().nullable(),
    catalogueUrl: z.string().nullable(),
    fees: guFees,
    shipping: guShipping,
    active: z.boolean(),
});
export const saveGuSeasonRequest = z.object({
    occasion: guOccasion,
    year: z.number().int().min(2020).max(2100),
    name: z.string().trim().min(1).max(100),
    salesFrom: day.nullable().optional(),
    salesTo: day.nullable().optional(),
    opensFrom: day.nullable().optional(),
    closesBy: day.nullable().optional(),
    catalogueUrl: z.string().trim().url().max(500).nullable().optional(),
    /** Omitted on create: Gaveuniverset's known values for the occasion. */
    fees: guFees.optional(),
    shipping: guShipping.optional(),
    active: z.boolean().default(true),
});
export const GU_ASSORTMENT_TAGS = ['budget', 'premade', 'sustainable', 'exclusive', 'homepage'];
export const GU_ASSORTMENT_TAG_LABELS = { budget: 'Budget', premade: 'Færdigbygget', sustainable: 'Bæredygtig', exclusive: 'Eksklusiv', homepage: 'Forside' };
export const guAssortmentDto = z.object({
    id: uuid,
    name: z.string(),
    occasion: guOccasion,
    giftPriceMinor: z.number().int(),
    maxRetailMinor: z.number().int().nullable(),
    budgetTitle: z.string().nullable(),
    favouriteTitle: z.string().nullable(),
    tags: z.array(z.string()),
    published: z.boolean(),
    position: z.number().int(),
    externalId: z.string().nullable(),
    productCount: z.number().int(),
    favouriteCount: z.number().int(),
});
export const guAssortmentDetailDto = guAssortmentDto.extend({
    products: z.array(z.object({ productId: uuid, title: z.string(), sku: z.string().nullable(), imageUrl: z.string().nullable(), prepared: z.boolean(), position: z.number().int() })),
});
export const saveGuAssortmentRequest = z.object({
    name: z.string().trim().min(1).max(120),
    occasion: guOccasion,
    giftPriceMinor: minor,
    maxRetailMinor: minor.nullable().optional(),
    budgetTitle: z.string().trim().max(120).nullable().optional(),
    favouriteTitle: z.string().trim().max(120).nullable().optional(),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
    published: z.boolean().default(false),
    position: z.number().int().default(100),
    products: z.array(z.object({ productId: uuid, prepared: z.boolean().default(false) })).max(2000).default([]),
});
export const guBuyerDto = z.object({
    id: uuid,
    companyName: z.string(),
    contactName: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    cvr: z.string().nullable(),
    ean: z.string().nullable(),
    street: z.string().nullable(),
    zip: z.string().nullable(),
    city: z.string().nullable(),
    countryCode: z.string(),
    status: z.enum(['active', 'disabled']),
    marketingConsent: z.boolean(),
    financeCustomerNumber: z.string().nullable(),
    hasPassword: z.boolean(),
    lastLoginAt: z.string().nullable(),
    createdAt: z.string(),
});
const opt = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v ? v : null));
export const saveGuBuyerRequest = z.object({
    companyName: z.string().trim().min(1).max(200),
    contactName: z.string().trim().min(1).max(200),
    email: z.string().trim().toLowerCase().email().max(200),
    phone: opt(40),
    cvr: z.string().trim().regex(/^\d{8}$/, 'CVR er 8 cifre').nullable().optional().or(z.literal('').transform(() => null)),
    ean: z.string().trim().regex(/^\d{13}$/, 'EAN er 13 cifre').nullable().optional().or(z.literal('').transform(() => null)),
    street: opt(200),
    zip: opt(20),
    city: opt(100),
    countryCode: z.string().trim().length(2).default('DK'),
    status: z.enum(['active', 'disabled']).default('active'),
    marketingConsent: z.boolean().default(false),
    financeCustomerNumber: opt(40),
});
export const guBuyerQuery = z.object({ q: z.string().trim().max(100).optional(), page: z.coerce.number().int().min(1).default(1), perPage: z.coerce.number().int().min(1).max(200).default(50) });
// --- buyer site: sign-up and login (GU-01) ------------------------------------------------------------
export const guRegisterRequest = z.object({
    companyName: z.string().trim().min(1).max(200),
    contactName: z.string().trim().min(1).max(200),
    email: z.string().trim().toLowerCase().email().max(200),
    phone: z.string().trim().min(6).max(40),
    cvr: z.string().trim().regex(/^\d{8}$/, 'CVR er 8 cifre'),
    ean: z.string().trim().regex(/^\d{13}$/, 'EAN er 13 cifre').nullable().optional().or(z.literal('').transform(() => null)),
    password: z.string().min(8).max(200),
    marketingConsent: z.boolean().default(false),
    acceptTerms: z.literal(true, { message: 'Accepter betingelserne' }),
    altcha: z.string().max(4000).optional(),
    /** Honeypot: people never fill it. */
    website: z.string().max(200).optional(),
});
export const guLoginRequest = z.object({ email: z.string().trim().toLowerCase().max(200), password: z.string().min(1).max(200) });
export const guResetRequest = z.object({ email: z.string().trim().toLowerCase().email().max(200), altcha: z.string().max(4000).optional() });
export const guSetPasswordRequest = z.object({ token: z.string().min(10).max(200), password: z.string().min(8).max(200) });
export const guTokenRequest = z.object({ token: z.string().min(10).max(200) });
/** The signed-in buyer as the buyer site sees it. */
export const guSessionBuyerDto = guBuyerDto.pick({ id: true, companyName: true, contactName: true, email: true, phone: true, cvr: true, ean: true, street: true, zip: true, city: true, countryCode: true, marketingConsent: true });
/** A CVR number looked up for the sign-up form (VIES; Danish companies only). */
export const guCvrLookupDto = z.object({ cvr: z.string(), valid: z.boolean(), name: z.string().nullable(), address: z.string().nullable(), alreadyBuyer: z.boolean() });
// --- buyer site: assortments, products and the shop being built (GU-02, GU-03) --------------------
export const guSiteDto = z.object({
    seasons: z.array(z.object({ id: uuid, occasion: guOccasion, name: z.string(), salesFrom: z.string().nullable(), salesTo: z.string().nullable(), catalogueUrl: z.string().nullable(), selling: z.boolean(), giftCountWarning: z.number().int(), giftCountWarningText: z.string() })),
    occasions: z.array(z.object({ occasion: guOccasion, label: z.string(), assortments: z.number().int() })),
});
export const guSiteAssortmentDto = z.object({
    id: uuid,
    name: z.string(),
    occasion: guOccasion,
    giftPriceMinor: z.number().int(),
    maxRetailMinor: z.number().int().nullable(),
    budgetTitle: z.string().nullable(),
    favouriteTitle: z.string().nullable(),
    tags: z.array(z.string()),
    productCount: z.number().int(),
    favouriteCount: z.number().int(),
    image: imageDto.nullable(),
    imageUrl: z.string().nullable(),
});
export const guSiteProductDto = z.object({
    id: uuid,
    title: z.string(),
    vendor: z.string().nullable(),
    image: imageDto.nullable(),
    imageUrl: z.string().nullable(),
    /** One of the assortment's favourites. */
    favourite: z.boolean(),
    sustainable: z.boolean(),
    exclusive: z.boolean(),
    /** 3x product groups ("PG: …" tags without the prefix), the builder's categories. */
    groups: z.array(z.string()),
});
export const guSiteProductQuery = z.object({
    q: z.string().trim().max(100).optional(),
    group: z.string().trim().max(100).optional(),
    vendor: z.string().trim().max(100).optional(),
    sustainable: z.coerce.boolean().optional(),
    exclusive: z.coerce.boolean().optional(),
    favourites: z.coerce.boolean().optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(500).default(48),
});
export const guSiteProductPageDto = z.object({
    assortment: guSiteAssortmentDto,
    items: z.array(guSiteProductDto),
    total: z.number().int(),
    page: z.number().int(),
    perPage: z.number().int(),
    /** Counts over the whole assortment, before filters. */
    filters: z.object({ groups: z.array(z.object({ name: z.string(), count: z.number().int() })), vendors: z.array(z.object({ name: z.string(), count: z.number().int() })), sustainable: z.number().int(), exclusive: z.number().int() }),
});
export const guDraftDto = z.object({
    token: z.string(),
    occasion: guOccasion.nullable(),
    assortmentId: uuid.nullable(),
    productIds: z.array(uuid),
    name: z.string().nullable(),
    updatedAt: z.string(),
});
export const saveGuDraftRequest = z.object({
    occasion: guOccasion.nullable().optional(),
    assortmentId: uuid.nullable().optional(),
    productIds: z.array(uuid).max(500).optional(),
    name: z.string().trim().max(120).nullable().optional(),
});
// --- buyer site: checkout (P3.3, GU-05, GU-06) -------------------------------------------------------
export const GU_CODE_TYPES = ['digital', 'physical'];
export const guCodeType = z.enum(GU_CODE_TYPES);
export const GU_CODE_TYPE_LABELS = { digital: 'Digitale gavekoder', physical: 'Fysiske gavekort' };
/** How the gifts reach the employees: to the company's addresses, or one parcel per employee. */
export const GU_DELIVERY_METHODS = ['business', 'home', 'parcel_shop'];
export const guDeliveryMethod = z.enum(GU_DELIVERY_METHODS);
export const GU_DELIVERY_LABELS = { business: 'Levering til virksomheden', home: 'Levering til medarbejdernes hjem', parcel_shop: 'Levering til pakkeshop' };
export const guPaymentMethod = z.enum(['invoice', 'card']);
/** What the quote needs; the checkout sends the same and more. */
export const guQuoteRequest = z.object({
    assortmentId: uuid,
    codes: z.number().int().min(1).max(20000),
    codeType: guCodeType.default('digital'),
    deliveryMethod: guDeliveryMethod.default('business'),
    /** Business delivery: the number of delivery addresses. */
    addresses: z.number().int().min(1).max(500).default(1),
    carryingSupport: z.boolean().default(false),
    paymentMethod: guPaymentMethod.default('invoice'),
});
export const GU_QUOTE_KEYS = ['gifts', 'adjust_fee', 'gift_card_freight', 'shipping', 'extra_addresses', 'carrying_support', 'invoice_fee'];
export const guQuoteLineDto = z.object({
    key: z.enum(GU_QUOTE_KEYS),
    label: z.string(),
    quantity: z.number().int(),
    /** Excl. VAT. */
    unitMinor: z.number().int(),
    totalMinor: z.number().int(),
});
/** Every amount is computed by the server (GU-06); the browser only shows it. */
export const guQuoteDto = z.object({
    season: z.object({ id: uuid, name: z.string(), occasion: guOccasion }).nullable(),
    assortment: z.object({ id: uuid, name: z.string(), giftPriceMinor: z.number().int() }),
    lines: z.array(guQuoteLineDto),
    subtotalMinor: z.number().int(),
    vatMinor: z.number().int(),
    totalMinor: z.number().int(),
    /** Reasons the order cannot be placed as it stands. */
    errors: z.array(z.string()),
    invoiceAllowed: z.boolean(),
    rules: z.object({
        earliestDelivery: z.string(),
        deliveryTo: z.string().nullable(),
        deliveryWeekdays: z.array(z.number().int()),
        opensFrom: z.string().nullable(),
        closesBy: z.string().nullable(),
        closeDaysBeforeDelivery: z.number().int(),
        /** Occasion shops can stay open with gifts sent as they are chosen. */
        continuousAllowed: z.boolean(),
        minimumRevenueMinor: z.number().int(),
        invoiceFromMinor: z.number().int(),
        freeExtraAddresses: z.number().int(),
        extraAddressFeeMinor: z.number().int(),
        carryingSupportMinor: z.number().int(),
        /** Price text per delivery method for the radio buttons. */
        shippingText: z.record(z.string(), z.string()),
    }),
});
const req = (max) => z.string().trim().min(1).max(max);
const optText = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v ? v : null));
const zip4 = z.string().trim().regex(/^\d{4}$/, 'Postnummer er 4 cifre');
export const guEmployeeInput = z.object({ name: req(200), email: z.string().trim().toLowerCase().email().max(200), department: optText(200) });
export const guAddressInput = z.object({
    company: req(200),
    contactName: req(200),
    street: req(200),
    zip: zip4,
    city: req(100),
    phone: optText(40),
    department: optText(200),
});
/** The placeholders the code mail text can use. */
export const GU_CODE_MAIL_PLACEHOLDERS = ['{{medarbejder}}', '{{gavekode}}', '{{virksomhed}}', '{{aabningsdato}}', '{{lukkedato}}', '{{link}}'];
export const guCheckoutRequest = guQuoteRequest.omit({ addresses: true }).extend({
    shopName: req(120),
    opensAt: day,
    /** Null with `continuous` (occasion shops). */
    closesAt: day.nullable().default(null),
    continuous: z.boolean().default(false),
    /** The week the gifts are sent; null with `continuous`. */
    deliveryDate: day.nullable().default(null),
    /** Codes to the employees by mail (needs the list), or all codes to the buyer. */
    sendCodesToEmployees: z.boolean().default(false),
    /** When the code mails go out; null = on the opening day. */
    codesSendAt: day.nullable().default(null),
    codeMailText: z.string().max(4000).default(''),
    employees: z.array(guEmployeeInput).max(20000).default([]),
    billing: z.object({
        companyName: req(200),
        contactName: req(200),
        cvr: z.string().trim().regex(/^\d{8}$/, 'CVR er 8 cifre'),
        ean: z.string().trim().regex(/^\d{13}$/, 'EAN er 13 cifre').nullable().optional().or(z.literal('').transform(() => null)),
        street: req(200),
        zip: zip4,
        city: req(100),
        email: z.string().trim().toLowerCase().email().max(200),
        phone: z.string().trim().min(6).max(40),
    }),
    /** Business delivery: one or more addresses (departments). */
    deliveryAddresses: z.array(guAddressInput).max(500).default([]),
    note: optText(2000),
    acceptTerms: z.literal(true, { message: 'Accepter handelsbetingelserne' }),
    marketingConsent: z.boolean().default(false),
});
export const guCheckoutResultDto = z.object({ number: z.string(), token: z.string(), paymentUrl: z.string().nullable() });
export const GU_ORDER_STATUSES = ['awaiting_payment', 'placed', 'provisioned', 'cancelled'];
export const GU_ORDER_STATUS_LABELS = { awaiting_payment: 'Afventer betaling', placed: 'Bestilt', provisioned: 'Gaveshop oprettet', cancelled: 'Annulleret' };
/** A buyer's order as the buyer site and the admin show it. */
export const guOrderDto = z.object({
    id: uuid,
    orderId: uuid,
    number: z.string(),
    token: z.string(),
    status: z.enum(GU_ORDER_STATUSES),
    placedAt: z.string(),
    buyer: z.object({ id: uuid, companyName: z.string(), email: z.string() }),
    shopName: z.string(),
    assortmentName: z.string(),
    occasion: guOccasion,
    codeType: guCodeType,
    codes: z.number().int(),
    deliveryMethod: guDeliveryMethod,
    carryingSupport: z.boolean(),
    opensAt: z.string(),
    closesAt: z.string().nullable(),
    continuous: z.boolean(),
    deliveryDate: z.string().nullable(),
    sendCodesToEmployees: z.boolean(),
    codesSendAt: z.string().nullable(),
    employeeCount: z.number().int(),
    deliveryAddresses: z.array(guAddressInput),
    billing: guCheckoutRequest.shape.billing,
    paymentMethod: guPaymentMethod,
    paid: z.boolean(),
    lines: z.array(guQuoteLineDto),
    subtotalMinor: z.number().int(),
    vatMinor: z.number().int(),
    totalMinor: z.number().int(),
    productCount: z.number().int(),
    invoiceStatus: z.string(),
    /** P3.4: the gift shop made for the order. */
    shop: z.object({ id: uuid, name: z.string(), slug: z.string().nullable(), url: z.string() }).nullable(),
    provisionedAt: z.string().nullable(),
    provisionError: z.string().nullable(),
    codesIssued: z.number().int(),
    /** Physical cards: the run of card numbers reserved for the order. */
    cards: z.object({ from: z.number().int(), to: z.number().int() }).nullable(),
    setup: z.object({ defaultProductId: uuid.nullable(), greeting: z.string().nullable(), logo: imageDto.nullable() }),
});
// --- codes, cards and provisioning (P3.4, GU-07, GU-08, GU-13) ----------------------------------------
/** Digital codes: 10 characters without the look-alikes 0/O, 1/I/L (about 50 bits). */
export const GU_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const GU_CODE_LENGTH = 10;
export const guCardPoolDto = z.object({
    free: z.number().int(),
    reserved: z.number().int(),
    void: z.number().int(),
    lowStock: z.number().int(),
    alertEmail: z.string(),
    batches: z.array(z.object({ batch: z.string().nullable(), count: z.number().int(), free: z.number().int(), fromSeq: z.number().int(), toSeq: z.number().int(), importedAt: z.string() })),
});
/**
 * The printer's file, pasted: one card per line, "number;code" (or tab or comma between), or just
 * the code, then numbered on from the highest number in the pool.
 */
export const importGuCardsRequest = z.object({ batch: z.string().trim().min(1).max(60), text: z.string().min(1).max(2_000_000) });
export const guCardImportResultDto = z.object({ imported: z.number().int(), skipped: z.number().int(), errors: z.array(z.string()) });
/** GU-08: the buyer's choices after ordering. */
export const saveGuSetupRequest = z.object({
    defaultProductId: uuid.nullable().optional(),
    greeting: z.string().trim().max(2000).nullable().optional(),
    logoImageId: uuid.nullable().optional(),
});
export const guSetupProductDto = z.object({ id: uuid, title: z.string(), image: imageDto.nullable(), imageUrl: z.string().nullable() });
// --- buyer self-service and staff screens (P3.5, GU-09 to GU-11, GU-14) ------------------------------
export const guShopSummaryDto = z.object({
    id: uuid,
    name: z.string(),
    url: z.string(),
    occasion: guOccasion,
    assortmentName: z.string(),
    /** upcoming before opening, open, closed after closing (never for continuous). */
    state: z.enum(['upcoming', 'open', 'closed']),
    opensAt: z.string().nullable(),
    closesAt: z.string().nullable(),
    continuous: z.boolean(),
    codeType: guCodeType,
    /** Codes in use now (ordered, less those given back). */
    codes: z.number().int(),
    chosen: z.number().int(),
    orders: z.number().int(),
    buyer: z.object({ id: uuid, companyName: z.string(), email: z.string() }),
});
export const guChoiceDto = z.object({
    code: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    department: z.string().nullable(),
    /** The chosen gift; null while the code is unused. */
    gift: z.string().nullable(),
    chosenAt: z.string().nullable(),
    status: z.string().nullable(),
});
export const guShopDetailDto = guShopSummaryDto.extend({
    orders: z.array(guOrderDto),
    choices: z.array(guChoiceDto),
    adjust: z.object({
        canBuyMore: z.boolean(),
        buyMoreReason: z.string().nullable(),
        canGiveBack: z.boolean(),
        giveBackReason: z.string().nullable(),
        /** Unused codes that can go back now. */
        maxGiveBack: z.number().int(),
        giftPriceMinor: z.number().int(),
        upFeeMinor: z.number().int(),
        downFeeMinor: z.number().int(),
        returned: z.number().int(),
    }),
});
export const guMoreQuoteRequest = z.object({ codes: z.number().int().min(1).max(20000), paymentMethod: guPaymentMethod.default('invoice') });
export const guMoreRequest = guMoreQuoteRequest.extend({
    /** Codes mailed to these employees (one per code), or all to the buyer when empty. */
    employees: z.array(guEmployeeInput).max(20000).default([]),
    acceptTerms: z.literal(true, { message: 'Accepter handelsbetingelserne' }),
});
export const guGiveBackRequest = z.object({ codes: z.number().int().min(1).max(20000) });
export const guRenameShopRequest = z.object({ name: z.string().trim().min(1).max(120) });
/** Staff: one row per Gaveuniverset shop with the ticks for the season's follow-up (GU-14). */
export const guStaffShopDto = guShopSummaryDto.extend({
    provisionedAt: z.string().nullable(),
    giftOrders: z.number().int(),
    packed: z.number().int(),
    shipped: z.number().int(),
    /** Every buyer order of the shop has an invoice / is paid (card captured or invoice paid). */
    invoiced: z.boolean(),
    paid: z.boolean(),
    totalMinor: z.number().int(),
});
// --- closing automation and reports (P3.6, GU-15, REP-04) ----------------------------------------------
/** Chosen gifts not yet released to the warehouse, set against what can be sold now, across open shops. */
export const guStockRowDto = z.object({
    variantId: uuid,
    sku: z.string(),
    title: z.string(),
    /** Gift orders waiting (chosen, not yet released, so not yet reserved). */
    waiting: z.number().int(),
    /** Of those, made automatically from the default gift. */
    autoGifts: z.number().int(),
    sellable: z.number().int(),
    shortfall: z.number().int(),
    shops: z.number().int(),
});
export const guClosingEventDto = z.object({
    shopId: uuid,
    shopName: z.string(),
    buyer: z.string(),
    closesAt: z.string(),
    /** reminder_7, reminder_3, close (default gifts and the closed mail) */
    kind: z.enum(['reminder_7', 'reminder_3', 'close']),
    on: z.string(),
    done: z.boolean(),
    codes: z.number().int(),
    chosen: z.number().int(),
    hasDefaultGift: z.boolean(),
});
export const guClosingRunDto = z.object({ reminders: z.number().int(), employeeReminders: z.number().int(), closed: z.number().int(), autoGifts: z.number().int(), withoutDefault: z.number().int(), report: z.boolean() });
//# sourceMappingURL=gaveuniverset.js.map